import {
    Box,
    Chip,
    Divider,
    Fade,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { VirtualElement } from "@popperjs/core";
import { KeyboardEvent, useEffect, useState } from "react";

import EditIcon from "@mui/icons-material/Edit";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import { DatabaseElement, Page } from "@/services/admin-services/models";
import { ArrowTurnDownLeftIcon, DocumentIcon, GlobeAltIcon } from "@heroicons/react/24/outline";
import { nanoid } from "nanoid";

interface LinkPopperProps {
    active: boolean;
    anchorEl: HTMLAnchorElement | null;
    availableElements: Array<DatabaseElement>;
    availablePages: Array<Page>;
    onEdit: (href: string, value: string, openInNewTab: boolean, data: Record<string, string>) => void;
    onRemove: () => void;
}

const LinkPopper = ({ active, anchorEl, availableElements, availablePages, onEdit, onRemove }: LinkPopperProps) => {
    const [anchor, setAnchor] = useState<VirtualElement | null>(null);
    const [url, setUrl] = useState<string>("");
    const [label, setLabel] = useState<string>("");
    const [openInNewTab, setOpenInNewTab] = useState<boolean>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [internalPageId, setInternalPageId] = useState<string>("");
    const [internalElementSlug, setInternalElementSlug] = useState<string>("");

    useEffect(() => {
        setOpenInNewTab(anchorEl?.getAttribute("target") === "_blank");
    }, [anchorEl]);

    useEffect(() => {
        if (active) {
            if (anchorEl) {
                setAnchor({
                    getBoundingClientRect: () => anchorEl?.getBoundingClientRect() || new DOMRect(),
                });
                return;
            }

            const selection = window.getSelection()?.getRangeAt(0);
            setAnchor({
                getBoundingClientRect: () => selection?.getBoundingClientRect() || new DOMRect(),
            });
        }
    }, [active, anchorEl]);

    useEffect(() => {
        if (!anchorEl) setIsEdit(true);

        if (anchorEl?.dataset["internalPageId"]) {
            const page = availablePages.find((p) => p.id === anchorEl.dataset["internalPageId"]);
            setUrl(`${page?.slug}`);
            setLabel(anchorEl.textContent || page?.title || "");
            setInternalPageId(anchorEl.dataset["internalPageId"]);
        } else if (anchorEl?.dataset["internalElementSlug"]) {
            const element = availableElements.find((e) => e.slug === anchorEl.dataset["internalElementSlug"]);
            setUrl(`${element?.slug}`);
            setLabel(anchorEl.textContent || element?.name || "");
            setInternalPageId("");
            setInternalElementSlug(anchorEl.dataset["internalElementSlug"]);
        } else {
            setUrl(anchorEl?.getAttribute("href") || "");
            setLabel(anchorEl?.textContent || "");
            setInternalPageId("");
            setInternalElementSlug("");
        }

        setOpenInNewTab(anchorEl?.getAttribute("target") === "_blank");

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchorEl, availablePages]);

    useEffect(() => {
        if (!anchorEl || !openInNewTab) return;

        anchorEl.target = openInNewTab ? "_blank" : "_self";

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openInNewTab]);

    const onEnterPressed = (e: KeyboardEvent<HTMLElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();

            onSubmit();
        }
    };

    const onSubmit = () => {
        let target = url;
        if (!internalPageId && !internalElementSlug && (!target.startsWith("http") || !target.startsWith("https"))) {
            target = `https://${target}`;
        }

        onEdit(target, label, openInNewTab!, { "data-link-type": "manual" });
    };

    const onSelectPage = (page: Page) => {
        onEdit(nanoid(), label.trim().length > 0 ? label : page.title, openInNewTab!, { "internal-page-id": page.id });
    };

    const onSelectElement = (element: DatabaseElement) => {
        onEdit(nanoid(), label.trim().length > 0 ? label : element.name, openInNewTab!, {
            "internal-element-slug": element.slug,
        });
    };

    const renderSearchResults = () => {
        const matchingPages = availablePages.filter(
            (page) =>
                page.title.toLowerCase().includes(url.toLowerCase()) || page.slug.includes(url.toLocaleLowerCase())
        );

        const matchingElements = availableElements.filter(
            (element) =>
                element.name.toLowerCase().includes(url.toLowerCase()) || element.slug.includes(url.toLocaleLowerCase())
        );

        return [
            matchingElements.map((element) => (
                <ListItemButton key={element.id} alignItems="flex-start" onClick={() => onSelectElement(element)}>
                    <ListItemIcon sx={{ minWidth: 48 }}>
                        <DocumentIcon className="h-5 w-5 ml-auto mr-2" />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Box sx={{ display: "flex" }}>
                                <Typography variant="body2" color="text.primary" sx={{ wordBreak: "break-word" }}>
                                    {element.name}
                                </Typography>
                                <Chip sx={{ ml: "auto", borderRadius: "4px" }} size="small" label="element" />
                            </Box>
                        }
                        secondary={
                            <Typography
                                sx={{ display: "inline", wordBreak: "break-word" }}
                                component="span"
                                variant="caption"
                                color="text.secondary"
                            >
                                elements/{element.slug}
                            </Typography>
                        }
                    />
                </ListItemButton>
            )),
            matchingPages.map((page) => (
                <ListItemButton key={page.id} alignItems="flex-start" onClick={() => onSelectPage(page)}>
                    <ListItemIcon sx={{ minWidth: 48 }}>
                        <DocumentIcon className="h-5 w-5 ml-auto mr-2" />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Box sx={{ display: "flex" }}>
                                <Typography variant="body2" color="text.primary" sx={{ wordBreak: "break-word" }}>
                                    {page.title}
                                </Typography>
                                <Chip sx={{ ml: "auto", borderRadius: "4px" }} size="small" label="page" />
                            </Box>
                        }
                        secondary={
                            <Typography
                                sx={{ display: "inline", wordBreak: "break-word" }}
                                component="span"
                                variant="caption"
                                color="text.secondary"
                            >
                                /{page.slug}
                            </Typography>
                        }
                    />
                </ListItemButton>
            )),
        ];
    };

    return active && anchor ? (
        <Popper
            className="link-popper"
            open={active}
            anchorEl={anchor}
            transition
            placement="bottom"
            sx={{ zIndex: 50 }}
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={100}>
                    <Paper sx={{ width: 400, border: "1px solid rgba(0, 0, 0, 0.125)" }} elevation={1}>
                        {isEdit ? (
                            <>
                                {anchorEl && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            p: (theme) => theme.spacing(2, 2, 0, 2),
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ width: 60 }}>
                                            TEXT
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Search or type URL"
                                            value={label}
                                            onChange={(e) => setLabel(e.target.value)}
                                            onKeyDown={(e) => onEnterPressed(e)}
                                        />
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        p: (theme) => theme.spacing(2, 2, 0, 2),
                                    }}
                                >
                                    <Typography variant="caption" sx={{ width: 60 }}>
                                        URL
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search or type URL"
                                        autoFocus
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton
                                                    disableRipple
                                                    onMouseDown={(e) => {
                                                        e.stopPropagation();
                                                        onSubmit();
                                                    }}
                                                    sx={{
                                                        color: "text.secondary",
                                                        "&:hover": { color: "text.primary" },
                                                    }}
                                                >
                                                    <ArrowTurnDownLeftIcon className="h-5 w-5" />
                                                </IconButton>
                                            ),
                                        }}
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                        onKeyDown={(e) => onEnterPressed(e)}
                                    />
                                </Box>
                            </>
                        ) : (
                            <List>
                                <ListItem alignItems="flex-start">
                                    <ListItemIcon sx={{ minWidth: 48 }}>
                                        <DocumentIcon className="h-5 w-5 ml-auto mr-2" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.primary"
                                                    fontWeight={700}
                                                    sx={{ wordBreak: "break-all" }}
                                                >
                                                    {label}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            <Typography
                                                sx={{ display: "inline", wordBreak: "break-all" }}
                                                component="span"
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {internalElementSlug && `elements/`}
                                                {url}
                                            </Typography>
                                        }
                                    />
                                    <ListItemIcon
                                        sx={{
                                            minWidth: "unset",
                                            mr: 1,
                                            cursor: "pointer",
                                            color: "text.secondary",
                                            "&:hover": { color: "text.primary" },
                                        }}
                                    >
                                        <EditIcon onMouseDown={() => setIsEdit(true)} />
                                    </ListItemIcon>
                                    <ListItemIcon
                                        sx={{
                                            minWidth: "unset",
                                            cursor: "pointer",
                                            color: "text.secondary",
                                            "&:hover": { color: "text.primary" },
                                        }}
                                    >
                                        <LinkOffIcon onMouseDown={() => onRemove()} />
                                    </ListItemIcon>
                                </ListItem>
                            </List>
                        )}

                        {isEdit && (
                            <Box sx={{ maxHeight: 200, overflow: "auto", minHeight: (theme) => theme.spacing(2) }}>
                                {url.length > 2 && (
                                    <List sx={{ p: 2 }}>
                                        {renderSearchResults()}

                                        <ListItemButton alignItems="flex-start" onClick={onSubmit}>
                                            <ListItemIcon sx={{ minWidth: 48 }}>
                                                <GlobeAltIcon className="h-5 w-5 ml-auto mr-2" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: "flex" }}>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.primary"
                                                            sx={{ wordBreak: "break-all", mr: 1 }}
                                                        >
                                                            {url}
                                                        </Typography>
                                                        <Chip
                                                            sx={{ ml: "auto", borderRadius: "4px" }}
                                                            size="small"
                                                            label="URL"
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Typography
                                                        sx={{ display: "inline" }}
                                                        component="span"
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Press ENTER to insert this link
                                                    </Typography>
                                                }
                                            />
                                        </ListItemButton>
                                    </List>
                                )}
                            </Box>
                        )}
                        {Boolean(anchorEl) && (
                            <>
                                <Divider />
                                <Typography variant="body2" sx={{ p: 1 }}>
                                    <Switch
                                        checked={Boolean(openInNewTab)}
                                        onKeyDown={(e) => onEnterPressed(e)}
                                        onChange={(e) => setOpenInNewTab(e.target.checked)}
                                    />{" "}
                                    Open in new tab
                                </Typography>
                            </>
                        )}
                    </Paper>
                </Fade>
            )}
        </Popper>
    ) : null;
};

export default LinkPopper;
